import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { pagePath } from '../lib/pages'
import { formatBitsPerSecond, formatNumber } from '../lib/format'
import {
  anordnen,
  KNOTEN_BREITE,
  KNOTEN_HOEHE,
  type KartenKnoten,
  type KartenVerbindung,
} from '../lib/topologieLayout'
import type { Topology, TopologyNode } from '../lib/types'

/** Wie ein Gerät auf der Karte steht: Stufe für die Farbe, Text für alle. */
export function kartenZustand(g: TopologyNode): {
  stufe: 'crit' | 'warn' | 'info' | 'off' | 'ok'
  text: string
} {
  const offen = g.alarme ?? 0
  if (g.zustand === 'crit' && !offen && (g.unterdrueckt ?? 0) > 0) {
    return { stufe: 'off', text: 'Folgefehler' }
  }
  if (offen > 0) {
    const stufe = g.schwere === 'crit' ? 'crit' : g.schwere === 'warn' ? 'warn' : 'info'
    const name = stufe === 'crit' ? 'Kritisch' : stufe === 'warn' ? 'Warnung' : 'Hinweis'
    return {
      stufe,
      text: `${name} · ${offen} ${offen === 1 ? 'Alarm' : 'Alarme'}`,
    }
  }
  if ((g.unterdrueckt ?? 0) > 0) return { stufe: 'off', text: 'Folgefehler oder Wartung' }
  if (g.zustand === 'crit') return { stufe: 'crit', text: 'Nicht erreichbar' }
  if (g.zustand === 'ok') return { stufe: 'ok', text: g.ip ?? 'Erreichbar' }
  return { stufe: 'off', text: g.ip ?? 'Unbekannt' }
}

const ERREICHBAR: Record<string, string> = {
  ok: 'Erreichbar',
  crit: 'Nicht erreichbar',
}

function kuerzen(text: string, zeichen: number): string {
  return text.length > zeichen ? `${text.slice(0, zeichen - 1)}…` : text
}

interface Props {
  topologie: Topology
  customerSlug: string | null
  /** Blendet Geräte ohne Alarm ab, damit die betroffenen hervortreten. */
  nurAlarme?: boolean
}

/**
 * Die Topologie als Karte: verwaltete Geräte mit ihrem Zustand und ihren
 * offenen Alarmen, unbekannte Nachbarn gestrichelt. Ein Gerät öffnet per
 * Klick oder Eingabetaste seine Seite; Zeiger oder Fokus zeigen die Alarme.
 */
export function TopologieKarte({ topologie, customerSlug, nurAlarme = false }: Props) {
  const navigate = useNavigate()
  const layout = useMemo(() => anordnen(topologie), [topologie])
  const flaeche = useRef<HTMLDivElement>(null)
  const [massstab, setMassstab] = useState(1)
  // Das aktive Gerät und der Bereich, der beim Aktivieren sichtbar war: die
  // Details öffnen sich zu der Seite, auf der Platz ist.
  const [aktiv, setAktivZustand] = useState<{ id: string; rechts: number } | null>(null)
  const pos = useMemo(() => new Map(layout.knoten.map((k) => [k.id, k])), [layout])

  const einpassen = () => {
    const breite = flaeche.current?.clientWidth ?? 0
    if (!breite || !layout.breite) return
    // Unter 70 % werden die Namen unlesbar; breitere Netze werden geschoben.
    setMassstab(Math.max(0.7, Math.min(1, Math.floor((breite / layout.breite) * 20) / 20)))
  }
  // Eine breite Karte passt sich beim ersten Anzeigen ein; danach entscheidet
  // die Person, wie groß sie sie haben will.
  const eingepasst = useRef(false)
  useLayoutEffect(() => {
    if (eingepasst.current || !layout.breite) return
    eingepasst.current = true
    einpassen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.breite])

  const setAktiv = (id: string | null) => {
    const f = flaeche.current
    setAktivZustand(id ? { id, rechts: f && f.clientWidth ? f.scrollLeft + f.clientWidth : Infinity } : null)
  }

  const oeffnen = (g: TopologyNode) => {
    void navigate({
      to: `${pagePath('geraete', g.kunde ?? customerSlug)}/${g.id}`,
    })
  }

  const aktiverKnoten = aktiv ? pos.get(aktiv.id) : undefined

  return (
    <div className="topo">
      <div className="topo-leiste">
        <Legende />
        <div className="row" style={{ gap: 6 }}>
          <button
            type="button"
            className="btn btn-sm"
            aria-label="Verkleinern"
            disabled={massstab <= 0.5}
            onClick={() => setMassstab((m) => Math.max(0.5, Math.round((m - 0.25) * 100) / 100))}
          >
            −
          </button>
          <span className="mono muted" style={{ fontSize: 12, minWidth: 42, textAlign: 'center' }}>
            {Math.round(massstab * 100)} %
          </span>
          <button
            type="button"
            className="btn btn-sm"
            aria-label="Vergrößern"
            disabled={massstab >= 2}
            onClick={() => setMassstab((m) => Math.min(2, Math.round((m + 0.25) * 100) / 100))}
          >
            +
          </button>
          <button type="button" className="btn btn-sm" onClick={einpassen}>
            Einpassen
          </button>
        </div>
      </div>
      <div className="topo-flaeche" ref={flaeche}>
        <div
          style={{
            position: 'relative',
            width: layout.breite * massstab,
            height: layout.hoehe * massstab,
          }}
        >
          <svg
            role="group"
            aria-label="Topologie-Karte"
            width={layout.breite * massstab}
            height={layout.hoehe * massstab}
            viewBox={`0 0 ${layout.breite} ${layout.hoehe}`}
          >
            {layout.verbindungen.map((v) => {
              const a = pos.get(v.a)
              const b = pos.get(v.b)
              if (!a || !b) return null
              const [oben, unten] = a.y <= b.y ? [a, b] : [b, a]
              const x1 = oben.x + KNOTEN_BREITE / 2
              const x2 = unten.x + KNOTEN_BREITE / 2
              const gleicheEbene = oben.y === unten.y
              const y1 = gleicheEbene ? oben.y + KNOTEN_HOEHE / 2 : oben.y + KNOTEN_HOEHE
              const y2 = gleicheEbene ? unten.y + KNOTEN_HOEHE / 2 : unten.y
              const gestoert = [a, b].some((k) => k.geraet?.zustand === 'crit') || v.stufe === 'crit'
              const belastet = !gestoert && v.stufe === 'warn'
              const hell = !nurAlarme || [a, b].some((k) => (k.geraet?.alarme ?? 0) > 0)
              const mitte = (y1 + y2) / 2
              const pfad = gleicheEbene
                ? `M ${x1} ${y1} L ${x2} ${y2}`
                : `M ${x1} ${y1} C ${x1} ${mitte}, ${x2} ${mitte}, ${x2} ${y2}`
              return (
                <path
                  key={`${v.a}|${v.b}`}
                  d={pfad}
                  className={`topo-linie${gestoert ? ' topo-linie-gestoert' : belastet ? ' topo-linie-belastet' : ''}`}
                  strokeDasharray={v.art === 'abhaengig' ? '5 4' : undefined}
                  opacity={hell ? 1 : 0.35}
                >
                  <title>
                    {[
                      v.art === 'abhaengig' ? 'Abhängigkeit (ohne LLDP)' : 'LLDP',
                      v.portA ? `${name(pos.get(v.a))} ${v.portA}` : '',
                      v.portB ? `${name(pos.get(v.b))} ${v.portB}` : '',
                      leitungsText(v),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </title>
                </path>
              )
            })}
            {layout.knoten.map((k) => (
              <Knoten
                key={k.id}
                k={k}
                gedimmt={nurAlarme && (k.geraet?.alarme ?? 0) === 0}
                onOeffnen={oeffnen}
                onAktiv={setAktiv}
              />
            ))}
          </svg>
          {aktiverKnoten?.geraet ? (
            <Details k={aktiverKnoten} massstab={massstab} sichtbarRechts={aktiv?.rechts ?? Infinity} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Zustand einer Leitung in Worten, für den Hinweis an der Linie. */
export function leitungsText(v: KartenVerbindung): string {
  if (v.link === 'down') return 'Link getrennt'
  if (v.auslastung === undefined) return v.link === 'up' ? 'Link aktiv' : ''
  const prozent = `Auslastung ${formatNumber(Math.round(v.auslastung), '%')}`
  if (v.bps === undefined || !v.mbit) return prozent
  return `${prozent} (${formatBitsPerSecond(v.bps)} von ${formatBitsPerSecond(v.mbit * 1e6)})`
}

function name(k?: KartenKnoten): string {
  return k?.geraet?.name ?? k?.fremdName ?? ''
}

function Knoten({
  k,
  gedimmt,
  onOeffnen,
  onAktiv,
}: {
  k: KartenKnoten
  gedimmt: boolean
  onOeffnen: (g: TopologyNode) => void
  onAktiv: (id: string | null) => void
}) {
  const g = k.geraet
  if (!g) {
    return (
      <g transform={`translate(${k.x} ${k.y})`} opacity={gedimmt ? 0.35 : 1}>
        <title>{`${k.fremdName} – nicht in der Netzwarte`}</title>
        <rect width={KNOTEN_BREITE} height={KNOTEN_HOEHE} rx={8} className="topo-knoten topo-knoten-fremd" />
        <text x={12} y={24} className="topo-name topo-name-fremd">
          {kuerzen(k.fremdName ?? '', 21)}
        </text>
        <text x={12} y={43} className="topo-text">
          nicht verwaltet
        </text>
      </g>
    )
  }
  const z = kartenZustand(g)
  const offen = g.alarme ?? 0
  const beschreibung = [g.name, ERREICHBAR[g.zustand] ?? 'Erreichbarkeit unbekannt', z.text].join(', ')
  return (
    <g
      role="link"
      tabIndex={0}
      aria-label={beschreibung}
      className="topo-geraet"
      transform={`translate(${k.x} ${k.y})`}
      opacity={gedimmt ? 0.35 : 1}
      onClick={() => onOeffnen(g)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOeffnen(g)
        }
      }}
      onMouseEnter={() => onAktiv(g.id)}
      onMouseLeave={() => onAktiv(null)}
      onFocus={() => onAktiv(g.id)}
      onBlur={() => onAktiv(null)}
    >
      <rect width={KNOTEN_BREITE} height={KNOTEN_HOEHE} rx={8} className={`topo-knoten topo-knoten-${z.stufe}`} />
      <circle
        cx={16}
        cy={20}
        r={4.5}
        className={`topo-punkt topo-punkt-${g.zustand === 'ok' ? 'ok' : g.zustand === 'crit' ? 'crit' : 'off'}`}
      />
      <text x={28} y={24} className="topo-name">
        {kuerzen(g.name, 19)}
      </text>
      <text x={12} y={44} className={`topo-text topo-text-${z.stufe}`}>
        {kuerzen(z.text, 26)}
      </text>
      {offen > 0 ? (
        <g transform={`translate(${KNOTEN_BREITE - 4} 4)`} aria-hidden="true">
          <circle r={11} className={`topo-zahl topo-zahl-${z.stufe}`} />
          <text textAnchor="middle" y={4} className="topo-zahl-text">
            {offen > 99 ? '99+' : offen}
          </text>
        </g>
      ) : null}
    </g>
  )
}

/** Breite der Details neben einem Gerät, in Bildpunkten. */
const DETAILS_BREITE = 280

function Details({ k, massstab, sichtbarRechts }: { k: KartenKnoten; massstab: number; sichtbarRechts: number }) {
  const g = k.geraet!
  const titel = g.alarm_titel ?? []
  const offen = g.alarme ?? 0
  // Rechts neben dem Gerät; reicht der sichtbare Bereich nicht, links davon.
  const rechtsDaneben = (k.x + KNOTEN_BREITE) * massstab + 8
  const left =
    rechtsDaneben + DETAILS_BREITE <= sichtbarRechts ? rechtsDaneben : Math.max(0, k.x * massstab - 8 - DETAILS_BREITE)
  return (
    <div className="topo-details" role="status" style={{ left, top: k.y * massstab }}>
      <div className="mono" style={{ fontWeight: 600 }}>
        {g.name}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {[g.typ, g.standort, g.ip].filter(Boolean).join(' · ')}
      </div>
      <div style={{ fontSize: 12, marginTop: 6 }}>{ERREICHBAR[g.zustand] ?? 'Erreichbarkeit unbekannt'}</div>
      {offen > 0 ? (
        <ul className="topo-alarme">
          {titel.map((t) => (
            <li key={t}>{t}</li>
          ))}
          {offen > titel.length ? <li className="muted">und {offen - titel.length} weitere</li> : null}
        </ul>
      ) : (
        <div className="muted" style={{ fontSize: 12 }}>
          Kein offener Alarm
        </div>
      )}
      {(g.unterdrueckt ?? 0) > 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>
          {g.unterdrueckt} zurückgehalten (Folgefehler oder Wartung)
        </div>
      ) : null}
    </div>
  )
}

function Legende() {
  return (
    <ul className="topo-legende" aria-label="Legende">
      <li>
        <span className="topo-muster topo-muster-crit" /> Kritisch
      </li>
      <li>
        <span className="topo-muster topo-muster-warn" /> Warnung
      </li>
      <li>
        <span className="topo-muster topo-muster-off" /> Folgefehler, Wartung, unbekannt
      </li>
      <li>
        <span className="topo-muster topo-muster-fremd" /> Nicht verwaltet
      </li>
      <li>
        <span className="topo-muster-linie" /> Abhängigkeit ohne LLDP
      </li>
      <li>
        <span className="topo-muster-linie topo-muster-linie-belastet" /> Leitung ab 70 % ausgelastet
      </li>
      <li>
        <span className="topo-muster-linie topo-muster-linie-gestoert" /> Leitung getrennt oder ab 90 %
      </li>
    </ul>
  )
}
